import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Breadcrumb, Card, Form, Input, Button, message, Typography, Select } from 'antd';

const { Option } = Select;
import * as Icon from 'react-feather';
import {
  curatedContentActionStarts,
  curatedContentActionErrors,
  getCuratedContentByIdAction,
  updateCuratedContentAction,
} from '../../core/data/redux/modules/curatedContent/actions';
import { getCuratedContentById, updateCuratedContent } from '../../services/curatedContentServices';

const { Title } = Typography;
const { TextArea } = Input;

const EditCuratedContent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { currentCuratedContent, loading } = useSelector((state: any) => state.curatedContent);
  const [form] = Form.useForm();
  const [initialValues, setInitialValues] = useState<any>(null);

  useEffect(() => {
    if (id) {
      fetchCuratedContent();
    }
  }, [id]);

  useEffect(() => {
    if (currentCuratedContent) {
      const values = {
        content: currentCuratedContent.content,
        type: currentCuratedContent.type,
        publish_status: currentCuratedContent.publish_status,
        image_url: currentCuratedContent.image_url,
        link_url: currentCuratedContent.link_url,
      };
      setInitialValues(values);
      form.setFieldsValue(values);
    }
  }, [currentCuratedContent, form]);

  const fetchCuratedContent = async () => {
    dispatch(curatedContentActionStarts());
    try {
      const res = await getCuratedContentById(id!);
      if (res.error) throw new Error(res.message || 'Error fetching curated content');
      dispatch(getCuratedContentByIdAction(res.data));
    } catch (err: any) {
      dispatch(curatedContentActionErrors(err.message));
      message.error(err.message);
    }
  };

  const onFinish = async (values: any) => {
    dispatch(curatedContentActionStarts());
    try {
      const contentData = {
        id: id,
        ...values,
      };
      
      const res = await updateCuratedContent(contentData);
      if (res.error === true) throw new Error(res.message || 'Error updating curated content');
      
      dispatch(updateCuratedContentAction(res.data));
      message.success('Curated content updated successfully');
      navigate('/admin/curated-content');
    } catch (err: any) {
      dispatch(curatedContentActionErrors(err.message));
      message.error(err.message);
    }
  };

  if (loading && !currentCuratedContent) return <Card loading style={{ margin: 24 }} />;

  return (
    <div className="page-wrapper page-settings" style={{ paddingTop: 0 }}>
      <div className="content">
        <Breadcrumb style={{ marginBottom: 20 }}>
          <Breadcrumb.Item>
            <a href="/admin/curated-content">Curated Content</a>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Edit Curated Content</Breadcrumb.Item>
        </Breadcrumb>
        
        <Card>
          <Title level={4} style={{ marginBottom: 24 }}>Edit Curated Content</Title>
          
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={initialValues}
          >
            <Form.Item
              label="Content"
              name="content"
              rules={[{ required: true, message: 'Please enter content' }]}
            >
              <TextArea rows={6} placeholder="Enter curated content..." />
            </Form.Item>

            <Form.Item
              label="Type"
              name="type"
              rules={[{ required: true, message: 'Please select content type' }]}
            >
              <Select>
                <Option value="TEXT">Text</Option>
                <Option value="IMAGE">Image</Option>
                <Option value="VIDEO">Video</Option>
                <Option value="LINK">Link</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="Publish Status"
              name="publish_status"
            >
              <Select>
                <Option value="DRAFT">Draft</Option>
                <Option value="PUBLISHED">Published</Option>
                <Option value="ARCHIVED">Archived</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="Image URL (Optional)"
              name="image_url"
            >
              <Input placeholder="Enter image URL" />
            </Form.Item>

            <Form.Item
              label="Link URL (Optional)"
              name="link_url"
            >
              <Input placeholder="Enter link URL" />
            </Form.Item>

            <Form.Item>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button type="primary" htmlType="submit" icon={<Icon.Save size={16} />}>
                  Update Curated Content
                </Button>
                <Button onClick={() => navigate('/admin/curated-content')}>
                  Cancel
                </Button>
              </div>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default EditCuratedContent;
